import {
  FluidIconConfig,
  FluidTheme,
  FluidThemeInterface,
} from '@lmig/fluid-core';
import { generateUniqueId } from '@lmig/fluid-core/lib/helpers/string.helpers';
import { FluidComponentDefinition } from '@lmig/fluid-core/lib/structs/interfaces/fluid-component-definition.interface';
import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Prop,
  Watch,
} from '@stencil/core';

import { ATTR } from '../../../constants/constants';
import { Utils } from '../../../utils/utils';

/**
 * @displayName Tab
 * @childOf fluid-tabs
 */
@Component({
  tag: 'fluid-tab',
  styleUrl: 'fluid-tab.less',
  shadow: true,
})
export class FluidTab implements ComponentInterface, FluidThemeInterface {
  // ================================================================ //
  // -- Host Element

  /**
   * Get a reference to the host element
   */
  @Element() host: HTMLFluidTabElement;

  // ================================================================ //
  // -- Native Attributes

  title: string;

  // ================================================================ //
  // -- Props
  /**
   * Optional alias for title attribute. Elements that implement a title attribute will cause some browsers
   * to display a tooltip of the title on hover, which is great for accessibility, but not so when elements in
   * the card have their own popovers. Providing an alias is preferable to explicitly removing the title attribute
   * from the attributes NamedNodeMap.
   * @type {string}
   */
  @Prop({ mutable: true }) tabTitle: string;

  /**
   * What theme settings should the component use?
   * @type {string}
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * If true, Tab will be "selected" within parent Tabs component.
   */
  @Prop({ mutable: true }) active: boolean;

  /**
   * FLUID Icon to be placed before the Tab label.
   */
  @Prop({ mutable: true }) iconBefore: FluidIconConfig;

  /**
   * FLUID Icon to be placed after the Tab label.
   */
  @Prop({ mutable: true }) iconAfter: FluidIconConfig;

  /**
   * Allows to disable the tab
   * @type {boolean}
   * @default {false}
   */
  @Prop({ mutable: true }) disabled = false;

  /**
   * If provided, will insert a fluid-component that uses this contextContentDefinition to render itself.
   */
  @Prop({ mutable: true }) contextContentDefinition: FluidComponentDefinition;

  @Watch('disabled')
  onDisabledChange(newValue: boolean, oldValue: boolean) {
    if (newValue !== oldValue) {
      this.disableChanged.emit(newValue);
    }
  }

  @Watch('tabTitle')
  onTabTitleChange(newValue: string, oldValue: string) {
    if (newValue !== oldValue) {
      this.titleChanged.emit(newValue);
    }
  }

  @Watch('hidden')
  onHiddenChange(newValue: string, oldValue: string) {
    if (newValue !== oldValue) {
      this.hiddenChanged.emit(newValue);
    }
  }

  /**
   * Event emitted when the property `disabled` was changed.
   */
  @Event() disableChanged: EventEmitter<boolean>;

  /**
   * Event emitted when the property `title` was changed.
   */
  @Event() titleChanged: EventEmitter<string>;

  /**
   * Event emitted when the property `hidden` was changed.
   */
  @Event() hiddenChanged: EventEmitter<string>;

  /**
   * If true the tab will be hidden
   */
  @Prop({ mutable: true }) hidden = false;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback() {
    Utils.setGlobalTheme(this);
    this.getSlottedIcons();
    this.host.id = this.host.id || generateUniqueId();
  }

  /**
   * Called once just after the component is first connected to the DOM.
   * @action: If the label is not set, we will get the title prop value from the native HTML attribute
   */
  componentWillLoad(): void {
    this.setTitle();
    Utils.watchAttr(ATTR.title, this.host, () => {
      this.setTitle();
      this.titleChanged.emit(this.title);
    });
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Sets the local card title attribute from the native HTML attribute
   */
  setTitle(): void {
    this.title = this.tabTitle || this.host.getAttribute(ATTR.title);
  }

  getSlottedIcons() {
    this._iconBefore = this.host.querySelector('[slot="icon-before"]');
    this._iconBefore?.remove();

    this._iconAfter = this.host.querySelector('[slot="icon-after"]');
    this._iconAfter?.remove();
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    const title = this.title || this.tabTitle;
    return (
      <Host
        role='tabpanel'
        aria-label={`${title}`}
        style={(!this.active || this.hidden) && { display: 'none' }}>
        <slot />
      </Host>
    );
  }

  // ================================================================ //
  // -- Internal Props
  //
  // -- Note: This is not a public API - these properties
  // -- should only be used internally by FLUID components.

  /**
   * Reference to slotted iconBefore (if it exists).
   * @internal
   */
  @Prop({ mutable: true }) _iconBefore: HTMLElement;

  /**
   * Reference to slotted iconAfter (if it exists).
   * @internal
   */
  @Prop({ mutable: true }) _iconAfter: HTMLElement;
}
